/**
 * AliasRegistryService - Distributed alias registry and peer discovery
 * Extracted from realP2PDemo_Stage2.html with V70 terminology compliance
 * Handles alias-to-peerId mapping and network-wide discovery
 */

import { AliasRegistration } from '../types'
import { DataConnection } from 'peerjs'

export interface AliasDiscoveryQuery {
  type: 'alias-query'
  queryId: string
  queryAlias: string  // Match original demo format
  from: string
  timestamp: number
}

export interface AliasDiscoveryResponse {
  type: 'alias-response'
  queryId: string
  queryAlias: string  // Match original demo format
  foundPeerId: string | null  // Match original demo format
  from: string
  timestamp: number
}

export interface AliasBroadcast {
  type: 'alias-broadcast'
  from: string
  alias: string
  timestamp: number
}

export class AliasRegistryService {
  private localAliasRegistry = new Map<string, string>() // alias -> peerId
  private peerToAliasMap = new Map<string, string>()     // peerId -> alias
  private discoveredAliases = new Map<string, {
    peerId: string
    discoveredThrough: string
    timestamp: number
  }>()

  private pendingQueries = new Map<string, {
    alias: string
    resolve: (peerId: string | null) => void
    reject: (error: Error) => void
    timeout: NodeJS.Timeout
  }>()

  private queryTimeout = 5000 // 5 seconds for alias discovery
  // private _maxQueryHops = 3
  private queryHistory = new Set<string>() // Prevent query loops
  private maxRetryAttempts = 3 // Max retry attempts for alias discovery
  private retryDelayBase = 1000 // Base delay for exponential backoff (1 second)

  // CRITICAL FIX: Track if we're using alias-as-peer-ID mode
  private isDirectAliasMode = false
  private removedAliases = new Set<string>() // Track explicitly removed aliases

  /**
   * Register a local alias (our own alias)
   * CRITICAL FIX: Detect if alias equals peerId (direct mode)
   */
  registerLocalAlias(alias: string, peerId: string): void {
    const cleanAlias = alias.trim()
    const cleanPeerId = peerId.trim()

    if (!cleanAlias || !cleanPeerId) {
      throw new Error('Alias and peerId cannot be empty')
    }

    // CRITICAL FIX: Detect direct alias mode
    this.isDirectAliasMode = (cleanAlias === cleanPeerId)

    // Remove old mapping if exists
    const oldPeerId = this.localAliasRegistry.get(cleanAlias)
    if (oldPeerId) {
      this.peerToAliasMap.delete(oldPeerId)
    }

    // Remove old alias for this peerId if exists
    const oldAlias = this.peerToAliasMap.get(cleanPeerId)
    if (oldAlias) {
      this.localAliasRegistry.delete(oldAlias)
    }

    this.localAliasRegistry.set(cleanAlias, cleanPeerId)
    this.peerToAliasMap.set(cleanPeerId, cleanAlias)

    if (this.isDirectAliasMode) {
      console.log(`🎯 Direct alias mode: ${cleanAlias} (alias IS peer ID)`)
    } else {
      console.log(`🏷️ Registry mode: ${cleanAlias} -> ${cleanPeerId.slice(0, 8)}...`)
    }
  }

  /**
   * Register a peer's alias (received from network)
   */
  registerPeerAlias(alias: string, peerId: string): void {
    const cleanAlias = alias.trim()
    const cleanPeerId = peerId.trim()
    
    if (!cleanAlias || !cleanPeerId) {
      return
    }

    // Don't overwrite our own alias
    const ourPeerId = this.getLocalPeerId()
    if (ourPeerId && cleanPeerId === ourPeerId) {
      return
    }

    // Update mappings
    this.localAliasRegistry.set(cleanAlias, cleanPeerId)
    this.peerToAliasMap.set(cleanPeerId, cleanAlias)
    
    // Also update discovered aliases cache
    this.discoveredAliases.set(cleanAlias, {
      peerId: cleanPeerId,
      discoveredThrough: 'direct',
      timestamp: Date.now()
    })
    
    console.log(`🌐 Peer alias registered: ${cleanAlias} -> ${cleanPeerId.slice(0, 8)}...`)
  }

  /**
   * Update a peer's alias
   */
  updatePeerAlias(newAlias: string, peerId: string): void {
    const cleanAlias = newAlias.trim()
    const cleanPeerId = peerId.trim()

    // Remove old alias mapping
    const oldAlias = this.peerToAliasMap.get(cleanPeerId)
    if (oldAlias) {
      this.localAliasRegistry.delete(oldAlias)
      this.discoveredAliases.delete(oldAlias)
      this.removedAliases.add(oldAlias) // Track as explicitly removed
    }

    // Register new alias
    this.registerPeerAlias(cleanAlias, cleanPeerId)
  }

  /**
   * Get peerId for a given alias
   * CRITICAL FIX: In direct mode, alias IS the peer ID
   */
  getPeerId(alias: string): string | null {
    const cleanAlias = alias.trim()

    // Check if this alias was explicitly removed
    if (this.removedAliases.has(cleanAlias)) {
      return null
    }

    // Check local registry first
    const localResult = this.localAliasRegistry.get(cleanAlias)
    if (localResult) {
      return localResult
    }

    // Check discovered aliases
    const discoveredResult = this.discoveredAliases.get(cleanAlias)
    if (discoveredResult) {
      return discoveredResult.peerId
    }

    // CRITICAL FIX: In direct alias mode, if not in registry,
    // the alias itself might be the peer ID - but only if it hasn't been explicitly removed
    if (this.isDirectAliasMode && this.isValidPeerId(cleanAlias)) {
      return cleanAlias
    }

    return null
  }

  /**
   * Check if a string could be a valid peer ID
   * Peer IDs are typically alphanumeric strings
   */
  private isValidPeerId(peerId: string): boolean {
    // Simple validation - peer IDs are usually alphanumeric
    // This is a heuristic to detect when an alias might be used directly as peer ID
    return /^[a-zA-Z0-9_-]+$/.test(peerId) && peerId.length >= 2
  }

  /**
   * Get the current connection mode
   */
  getConnectionMode(): 'direct' | 'registry' {
    return this.isDirectAliasMode ? 'direct' : 'registry'
  }

  /**
   * Get alias for a given peerId
   */
  getAlias(peerId: string): string | null {
    return this.peerToAliasMap.get(peerId.trim()) || null
  }

  /**
   * Get our local peerId (assuming we only have one local registration)
   */
  getLocalPeerId(): string | null {
    for (const [peerId, alias] of this.peerToAliasMap) {
      // Our local peerId should be the one we registered first
      if (this.localAliasRegistry.get(alias) === peerId) {
        return peerId
      }
    }
    return null
  }

  /**
   * Get all registered aliases
   */
  getAllAliases(): AliasRegistration[] {
    const registrations: AliasRegistration[] = []
    const now = Date.now()
    
    for (const [alias, peerId] of this.localAliasRegistry) {
      const discovered = this.discoveredAliases.get(alias)
      registrations.push({
        alias,
        peerId,
        timestamp: new Date(discovered?.timestamp || now),
        isActive: true // We could implement a heartbeat system here
      })
    }
    
    return registrations
  }

  /**
   * Discover an alias across the P2P network
   */
  async discoverAlias(
    alias: string,
    connections: Map<string, { connection: DataConnection; info: any }>
  ): Promise<string | null> {
    const cleanAlias = alias.trim()

    // First check local registry
    const localPeerId = this.getPeerId(cleanAlias)
    if (localPeerId) {
      return localPeerId
    }

    // Check discovered aliases cache
    const cachedAlias = this.discoveredAliases.get(cleanAlias)
    if (cachedAlias) {
      // Verify cache isn't too old (5 minutes)
      if (Date.now() - cachedAlias.timestamp < 300000) {
        return cachedAlias.peerId
      } else {
        this.discoveredAliases.delete(cleanAlias)
      }
    }

    // No active connections to query
    if (connections.size === 0) {
      return null
    }

    return this.queryAliasFromNetwork(cleanAlias, connections)
  }

  /**
   * Enhanced alias discovery with retry logic and exponential backoff
   */
  async discoverAliasWithRetry(
    alias: string,
    connections: Map<string, { connection: DataConnection; info: any }>,
    onRetryProgress?: (attempt: number, maxAttempts: number) => void
  ): Promise<string | null> {
    const cleanAlias = alias.trim()

    // First check local registry
    const localPeerId = this.getPeerId(cleanAlias)
    if (localPeerId) {
      return localPeerId
    }

    // Check discovered aliases cache first
    const cachedAlias = this.discoveredAliases.get(cleanAlias)
    if (cachedAlias) {
      // Verify cache isn't too old (5 minutes)
      if (Date.now() - cachedAlias.timestamp < 300000) {
        return cachedAlias.peerId
      } else {
        this.discoveredAliases.delete(cleanAlias)
      }
    }

    // No active connections to query
    if (connections.size === 0) {
      console.log(`❌ No active connections available for alias discovery`)
      return null
    }

    // Retry logic with exponential backoff
    for (let attempt = 1; attempt <= this.maxRetryAttempts; attempt++) {
      console.log(`🔍 Alias discovery attempt ${attempt}/${this.maxRetryAttempts} for '${cleanAlias}'`)

      if (onRetryProgress) {
        onRetryProgress(attempt, this.maxRetryAttempts)
      }

      const result = await this.queryAliasFromNetwork(cleanAlias, connections)

      if (result) {
        console.log(`✅ Alias '${cleanAlias}' discovered on attempt ${attempt}: ${result.slice(0, 8)}...`)
        return result
      }

      // If not the last attempt, wait before retrying
      if (attempt < this.maxRetryAttempts) {
        const delay = this.retryDelayBase * Math.pow(2, attempt - 1) // Exponential backoff
        console.log(`⏳ Waiting ${delay}ms before retry attempt ${attempt + 1}...`)
        await this.sleep(delay)
      }
    }

    console.log(`❌ Alias '${cleanAlias}' not found after ${this.maxRetryAttempts} attempts`)
    return null
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Query the network for an alias
   */
  private async queryAliasFromNetwork(
    alias: string,
    connections: Map<string, { connection: DataConnection; info: any }>
  ): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const queryId = `query-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`
      
      const query: AliasDiscoveryQuery = {
        type: 'alias-query',
        queryId,
        queryAlias: alias,
        from: this.getLocalPeerId() || 'unknown',
        timestamp: Date.now()
      }

      // Set up timeout
      const timeout = setTimeout(() => {
        this.pendingQueries.delete(queryId)
        resolve(null) // Return null if no response received
      }, this.queryTimeout)

      // Store pending query
      this.pendingQueries.set(queryId, {
        alias,
        resolve,
        reject,
        timeout
      })

      // Broadcast query to all connected peers
      let queryCount = 0
      for (const [peerId, connectionData] of connections) {
        if (connectionData.connection.open) {
          try {
            connectionData.connection.send(query)
            queryCount++
          } catch (error) {
            console.warn(`Failed to send alias query to ${peerId}:`, error)
          }
        }
      }

      if (queryCount === 0) {
        clearTimeout(timeout)
        this.pendingQueries.delete(queryId)
        resolve(null)
      }

      console.log(`🔍 Querying ${queryCount} peers for alias '${alias}'...`)
    })
  }

  /**
   * Handle incoming alias query from another peer
   */
  handleAliasQuery(
    query: AliasDiscoveryQuery,
    fromConnection: DataConnection
  ): void {
    // Prevent query loops
    if (this.queryHistory.has(query.queryId)) {
      return
    }

    // Add to history to prevent loops
    this.queryHistory.add(query.queryId)
    
    // Clean up old query history (keep last 100)
    if (this.queryHistory.size > 100) {
      const entries = Array.from(this.queryHistory)
      this.queryHistory.clear()
      entries.slice(-50).forEach(id => this.queryHistory.add(id))
    }

    const foundPeerId = this.getPeerId(query.queryAlias)
    
    const response: AliasDiscoveryResponse = {
      type: 'alias-response',
      queryId: query.queryId,
      queryAlias: query.queryAlias,
      foundPeerId,
      from: this.getLocalPeerId() || 'unknown',
      timestamp: Date.now()
    }

    try {
      fromConnection.send(response)
    } catch (error) {
      console.warn('Failed to send alias query response:', error)
    }

    console.log(`❓ Alias query for '${query.queryAlias}': ${foundPeerId ? 'found' : 'not found'}`)
  }

  /**
   * Handle incoming alias query response
   */
  handleAliasResponse(response: AliasDiscoveryResponse): void {
    const pendingQuery = this.pendingQueries.get(response.queryId)
    if (!pendingQuery) {
      return // Query might have timed out
    }

    clearTimeout(pendingQuery.timeout)
    this.pendingQueries.delete(response.queryId)

    if (response.foundPeerId) {
      // Cache the discovered alias
      this.discoveredAliases.set(response.queryAlias, {
        peerId: response.foundPeerId,
        discoveredThrough: response.from,
        timestamp: response.timestamp
      })

      // Also register in local registry for faster future lookups
      this.registerPeerAlias(response.queryAlias, response.foundPeerId)
      
      console.log(`✅ Alias '${response.queryAlias}' discovered: ${response.foundPeerId.slice(0, 8)}...`)
      pendingQuery.resolve(response.foundPeerId)
    } else {
      console.log(`❌ Alias '${response.queryAlias}' not found on network`)
      pendingQuery.resolve(null)
    }
  }

  /**
   * Broadcast alias update to connected peers
   */
  broadcastAliasUpdate(
    alias: string,
    connections: Map<string, { connection: DataConnection; info: any }>
  ): void {
    const broadcast: AliasBroadcast = {
      type: 'alias-broadcast',
      from: this.getLocalPeerId() || 'unknown',
      alias: alias.trim(),
      timestamp: Date.now()
    }

    let broadcastCount = 0
    for (const [peerId, connectionData] of connections) {
      if (connectionData.connection.open) {
        try {
          connectionData.connection.send(broadcast)
          broadcastCount++
        } catch (error) {
          console.warn(`Failed to broadcast alias to ${peerId}:`, error)
        }
      }
    }

    console.log(`📢 Broadcasted alias '${alias}' to ${broadcastCount} peers`)
  }

  /**
   * Handle incoming alias broadcast
   */
  handleAliasBroadcast(broadcast: AliasBroadcast): void {
    if (broadcast.from && broadcast.alias) {
      this.registerPeerAlias(broadcast.alias, broadcast.from)
      console.log(`📡 Received alias broadcast: ${broadcast.alias} -> ${broadcast.from.slice(0, 8)}...`)
    }
  }

  /**
   * Handle incoming P2P data (router method)
   */
  handleP2PData(data: unknown, fromConnection: DataConnection): boolean {
    if (typeof data !== 'object' || !data) {
      return false
    }

    const typedData = data as { type?: string }
    
    switch (typedData.type) {
      case 'alias-query':
        this.handleAliasQuery(typedData as AliasDiscoveryQuery, fromConnection)
        return true
      
      case 'alias-response':
        this.handleAliasResponse(typedData as AliasDiscoveryResponse)
        return true
      
      case 'alias-broadcast':
        this.handleAliasBroadcast(typedData as AliasBroadcast)
        return true
      
      default:
        return false // Not handled by this service
    }
  }

  /**
   * Clean up expired discoveries and queries
   */
  cleanup(): void {
    const now = Date.now()
    const maxAge = 600000 // 10 minutes
    
    // Clean up old discovered aliases
    for (const [alias, discovery] of this.discoveredAliases) {
      if (now - discovery.timestamp > maxAge) {
        this.discoveredAliases.delete(alias)
      }
    }
    
    // Cancel any pending queries
    for (const [_queryId, query] of this.pendingQueries) {
      clearTimeout(query.timeout)
      query.resolve(null)
    }
    this.pendingQueries.clear()
    
    console.log('AliasRegistryService cleanup completed')
  }

  /**
   * Get statistics about the alias registry
   */
  getStats(): {
    localAliases: number
    discoveredAliases: number
    pendingQueries: number
    queryHistory: number
  } {
    return {
      localAliases: this.localAliasRegistry.size,
      discoveredAliases: this.discoveredAliases.size,
      pendingQueries: this.pendingQueries.size,
      queryHistory: this.queryHistory.size
    }
  }

  /**
   * Clear all data (for testing or reset)
   */
  reset(): void {
    this.localAliasRegistry.clear()
    this.peerToAliasMap.clear()
    this.discoveredAliases.clear()
    this.queryHistory.clear()
    this.removedAliases.clear()
    this.cleanup()
    console.log('AliasRegistryService reset completed')
  }
}